// Single source of truth for Amazon account-type classification.
//
// Pure, profile-level predicates: each takes any profile/marketplace object
// (carrying `accountType` / `accountSubType`) and behaves identically whether
// that object is a marketplace-dropdown option, an AG Grid row, or the active
// marketplace. The account store wraps these as reactive getters for the active
// marketplace (`isSellerMarketplace`, `isVendorMarketplace`, `isBookMarketplace`,
// `isSpEligibleMarketplace`, `currentAccountType`) — prefer those in components.
//
// Do NOT re-derive `accountType === 'SELLER'` (etc.) anywhere else — add the
// rule here and let callers consume the predicate or the store getter.
//
// Why it matters: SELLER vs VENDOR vs KDP author drive real behavior —
// Seller-Central deep links are SELLER-only (VENDOR / KDP_AUTHOR land on a "no
// access" page, WP-718), Selling Partner coverage is seller+vendor *excluding*
// KDP authors, and the type marker icon differs per type.
//
// Read type and subType as two independent axes, the way Amazon models them:
// accountInfo.type is seller | vendor | agency, accountInfo.subType is '' |
// KDP_AUTHOR | AMAZON_ATTRIBUTION. KDP is a subType overlay that rides on top of
// a type — Amazon's own profiles reference documents {type: 'vendor', subType:
// 'KDP_AUTHOR'} — so a book profile must be detected by subType and never by type.

export const ACCOUNT_TYPE = Object.freeze({
  SELLER: 'SELLER',
  VENDOR: 'VENDOR',
});

// A book / Kindle Direct Publishing author is flagged by this account subtype.
// The manager API types `account_sub_type` as a free string (no enum), so this
// is the de-facto frontend marker.
export const KDP_AUTHOR_SUBTYPE = 'KDP_AUTHOR';

// The single semantic dimension the capability registry gates on. SELLER/VENDOR
// mirror ACCOUNT_TYPE; BOOK is a KDP subType overlay promoted to its own caste.
export const MARKETPLACE_CATEGORY = Object.freeze({
  SELLER: 'SELLER',
  VENDOR: 'VENDOR',
  BOOK: 'BOOK',
});

export const normalizeAccountType = (profile) =>
  (profile?.accountType || '').toString().toUpperCase();

export const isSellerProfile = (profile) =>
  normalizeAccountType(profile) === ACCOUNT_TYPE.SELLER;

export const isVendorProfile = (profile) =>
  normalizeAccountType(profile) === ACCOUNT_TYPE.VENDOR;

export const isBookProfile = (profile) => profile?.accountSubType === KDP_AUTHOR_SUBTYPE;

// Single semantic category for a profile/marketplace — the dimension the
// capability registry (config/marketplaceCapabilities.js) gates on. BOOK takes
// precedence because KDP is a subType overlay (a KDP author may also report as
// vendor/seller, but we treat it as its own caste). Returns null when the type
// is unknown (e.g. while account data is still loading).
export const marketplaceCategory = (profile) => {
  if (!profile) return null;
  if (isBookProfile(profile)) return MARKETPLACE_CATEGORY.BOOK;
  if (isVendorProfile(profile)) return MARKETPLACE_CATEGORY.VENDOR;
  if (isSellerProfile(profile)) return MARKETPLACE_CATEGORY.SELLER;
  return null;
};

// Selling Partner (SP-API / Data Kiosk) coverage applies to seller + vendor
// accounts. Derived from marketplaceCategory rather than from accountType so BOOK
// wins here exactly as it does for capability gating: Amazon publishes no SP-API
// data for Kindle Direct Publishing at all, so a KDP author has nothing to cover
// even when it reports accountType VENDOR. Reading accountType directly made the
// same profile both BOOK and SP-eligible, which are contradictory answers.
export const isSpEligibleProfile = (profile) => {
  const category = marketplaceCategory(profile);
  return category === MARKETPLACE_CATEGORY.SELLER || category === MARKETPLACE_CATEGORY.VENDOR;
};

// Does this business profile advertise any Kindle Direct Publishing title?
// Takes the V1 businessProfiles shape (amazonAdsAccounts[].amazonAdsProfiles[]).
export const businessProfileHasBook = (businessProfile) =>
  businessProfile?.amazonAdsAccounts?.some((account) =>
    account.amazonAdsProfiles?.some(isBookProfile),
  ) ?? false;
